import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/data/content";
import api, { formatApiError } from "@/lib/api";
import { LogoMark } from "./Navbar";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ type: null, msg: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ type: "loading", msg: "" });
    try {
      await api.post("/newsletter", { email });
      setStatus({ type: "success", msg: "Subscribed. Thank you." });
      setEmail("");
    } catch (err) {
      setStatus({ type: "error", msg: formatApiError(err?.response?.data?.detail) || "Could not subscribe" });
    }
  };

  return (
    <footer className="bg-[#0A0A0A] text-white" data-testid="site-footer">
      {/* Newsletter band */}
      <section className="border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 py-12 md:py-16 grid lg:grid-cols-2 gap-10 items-end">
          <div>
            <div className="mono-label text-zinc-500">Newsletter · 01</div>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl mt-4 leading-[1.05] tracking-tight text-balance">
              Projects, thinking and jobs — in your inbox, quarterly.
            </h3>
          </div>
          <form onSubmit={submit} className="flex flex-col gap-3" data-testid="newsletter-form">
            <div className="flex flex-col sm:flex-row gap-3 border-b border-zinc-700 pb-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="flex-1 bg-transparent text-lg md:text-xl font-display placeholder:text-zinc-600 focus:outline-none py-2"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                className="btn-outline-light self-start sm:self-auto"
                disabled={status.type === "loading"}
                data-testid="newsletter-submit"
              >
                {status.type === "loading" ? "Subscribing…" : "Subscribe"}
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
            {status.type === "success" && (
              <div className="mono-label text-emerald-400" data-testid="newsletter-success">
                ✓ {status.msg}
              </div>
            )}
            {status.type === "error" && (
              <div className="mono-label text-red-400" data-testid="newsletter-error">
                ✕ {status.msg}
              </div>
            )}
            <p className="text-xs text-zinc-500">By subscribing you agree to receive occasional emails from Mohr Engineering. Unsubscribe anytime.</p>
          </form>
        </div>
      </section>

      {/* Main grid */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 py-12 md:py-16 grid md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10">
        <div className="lg:col-span-5">
          <LogoMark className="w-20 h-20 md:w-24 md:h-24" />
          <p className="text-zinc-400 mt-5 max-w-md leading-relaxed text-[15px]">
            Abuja-based engineering & general contracting firm delivering turnkey civil, institutional and residential projects across Nigeria. We build experiences.
          </p>
        </div>

        <div className="lg:col-span-3">
          <div className="mono-label text-zinc-500 mb-4">Navigate</div>
          <ul className="space-y-2">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-zinc-200 hover:text-white text-base font-display tracking-tight" data-testid={`footer-nav-${l.label.toLowerCase().replace(/ /g, "-")}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-4">
          <div className="mono-label text-zinc-500 mb-4">Contact</div>
          <address className="not-italic text-zinc-300 space-y-2 text-sm leading-relaxed">
            <div>{COMPANY.location}</div>
            <div className="flex flex-col gap-1">
              {COMPANY.phones.map((p) => (
                <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="link-underline w-fit">{p}</a>
              ))}
            </div>
            <div className="flex flex-col gap-1">
              {COMPANY.emails.map((e) => (
                <a key={e} href={`mailto:${e}`} className="link-underline w-fit">{e}</a>
              ))}
            </div>
          </address>
        </div>
      </div>

      <div className="border-t border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="mono-label text-zinc-500">© {new Date().getFullYear()} Mohr Engineering Limited · All rights reserved</div>
          <div className="mono-label text-zinc-500 flex gap-6">
            <Link to="/admin/login" data-testid="footer-admin-link">Admin</Link>
            <span>RC Nigeria</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
